import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import config from '../config';

const { BASE_URL } = config;

const STORAGE_KEY = 'stackGenJob';
const POLL_INTERVAL_MS = 3000;
// Ceiling kept in lockstep with the server's pending TTL (90s, server.py:get_job_status)
// so the client never keeps polling a job the backend has already declared timed out.
const MAX_POLL_ATTEMPTS = 30; // 30 * 3s = 90s
const MAX_CONSECUTIVE_FAILURES = 3; // tolerate transient network blips before giving up

// Sentinel ownerUid for a job created before Firebase auth finished resolving (so
// we couldn't yet tell a signed-in user from a guest). On the first auth callback
// it's resolved to the session's real uid (or null for a genuine guest). This lets
// us adopt a pre-auth job for its rightful owner WITHOUT adopting a true guest's
// job for whoever signs in next (which would leak the guest's result).
const AUTH_PENDING = '__auth_pending__';

// status lifecycle: 'idle' | 'starting' | 'pending' | 'completed' | 'failed' | 'expired'
// 'starting' = generation requested but the backend hasn't returned a job_id yet
// (can be many seconds on a cold container). Tracking it means the in-progress
// indicator shows immediately and survives navigating away during the wait.
const IDLE_STATE = {
  jobId: null,
  healthGoal: '',
  // Which flow owns this job: 'generate' (build me a stack) or 'analyze'
  // (tell me about the stack I typed in). One app-wide job slot is shared by
  // both, and their results have different shapes, so a page must be able to
  // tell whether the finished job is the one it started. Without this,
  // navigating from an in-flight analysis to /create-stack hands the analysis
  // payload to the generator's consume effect, which reads it as a generated
  // stack. Defaults to 'generate' so every existing caller is unchanged.
  kind: 'generate',
  // Which form started this job, as an opaque key the starter chooses — not
  // the user (that is `ownerUid`). `kind` alone cannot answer "is this mine?"
  // when one component serves several pages: `StackForm` is both the analysis
  // page and every `/edit-stack/:id`, so an analysis started on one stack was
  // consumed by whichever of them mounted next and merged into that stack's
  // rows (STACK-544). Null for the generator, which has one page and does not
  // need it.
  startedBy: null,
  status: 'idle',
  result: null,
  pollCount: 0,
  error: null,
  // Latest live progress object from /job_status while pending (stage, label,
  // pct, done/total, events[]) — optional, absent on older backends. See
  // docs/stack-generation/live-progress.md.
  progress: null,
  // uid that owns this job (null for a guest). Used to drop the persisted job when
  // a different user takes over the tab, so one user never sees another's result.
  ownerUid: null,
};

const loadInitialState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // A 'starting' job persisted across a page reload can't be resumed: the POST
      // that would mint its job_id died with the old page. Surface a soft retry
      // rather than a spinner that never resolves.
      if (parsed.status === 'starting') {
        return {
          ...IDLE_STATE,
          status: 'failed',
          healthGoal: parsed.healthGoal || '',
          kind: parsed.kind || 'generate',
          startedBy: parsed.startedBy ?? null,
          error: 'Your stack didn’t finish starting. Please try again.',
          ownerUid: parsed.ownerUid ?? null,
        };
      }
      return { ...IDLE_STATE, ...parsed };
    }
  } catch (e) {
    // Corrupted payload — fall back to idle and let it be overwritten.
  }
  return { ...IDLE_STATE };
};

const StackGenerationContext = createContext();

export const useStackGeneration = () => {
  const context = useContext(StackGenerationContext);
  if (!context) {
    throw new Error('useStackGeneration must be used within a StackGenerationProvider');
  }
  return context;
};

export const StackGenerationProvider = ({ children }) => {
  const [job, setJob] = useState(loadInitialState);
  const jobRef = useRef(job);
  jobRef.current = job;
  const pollTimerRef = useRef(null);
  const failuresRef = useRef(0);
  // Flips true on the first onAuthStateChanged callback, so startJob knows whether
  // it can trust getAuth().currentUser yet.
  const authResolvedRef = useRef(false);
  // Gate: a non-idle job rehydrated from sessionStorage on mount stays hidden from
  // consumers (and unpolled) until the first auth callback validates/clears its
  // owner — otherwise a different user could briefly see the previous user's result
  // before the async auth check runs. Jobs created in-session (startJob) are owned
  // by the current session and are never gated.
  const [ownerValidated, setOwnerValidated] = useState(job.status === 'idle');

  // Merge a partial update into job state and mirror it to sessionStorage so a
  // pending OR completed job survives navigation / reload within the session.
  const update = useCallback((partial) => {
    setJob((prev) => {
      const next = { ...prev, ...partial };
      jobRef.current = next;
      try {
        if (next.status === 'idle') {
          sessionStorage.removeItem(STORAGE_KEY);
        } else {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        }
      } catch (e) {
        // sessionStorage unavailable (private mode / quota) — state still lives in memory.
      }
      return next;
    });
  }, []);

  // Begin tracking a freshly-created async job. The polling effect picks this up.
  const startJob = useCallback((jobId, healthGoal, kind = 'generate', startedBy = null) => {
    failuresRef.current = 0;
    setOwnerValidated(true); // in-session job — current user owns it, no gate
    update({
      jobId,
      healthGoal: healthGoal || '',
      kind,
      startedBy,
      status: 'pending',
      result: null,
      pollCount: 0,
      error: null,
      progress: null,
      // If auth has resolved, currentUser is authoritative (uid, or null = real
      // guest). If not, stamp the sentinel and let the auth listener resolve it.
      ownerUid: authResolvedRef.current ? (getAuth().currentUser?.uid || null) : AUTH_PENDING,
    });
  }, [update]);

  // Mark that a generation is being kicked off, BEFORE the POST that mints the
  // job_id returns. On a cold backend that POST can take many seconds; recording
  // 'starting' now means the in-progress indicator shows immediately and the state
  // survives navigating away during the wait. startJob() promotes it to 'pending'
  // once the job_id arrives.
  const beginGenerating = useCallback((healthGoal, kind = 'generate', startedBy = null) => {
    failuresRef.current = 0;
    setOwnerValidated(true); // in-session — current user owns it, no gate
    update({
      jobId: null,
      healthGoal: healthGoal || '',
      kind,
      startedBy,
      status: 'starting',
      result: null,
      pollCount: 0,
      error: null,
      progress: null,
      ownerUid: authResolvedRef.current ? (getAuth().currentUser?.uid || null) : AUTH_PENDING,
    });
  }, [update]);

  // Reset to idle — used after a completed result is consumed, on cancel, or after
  // an error is acknowledged.
  const clearJob = useCallback(() => {
    failuresRef.current = 0;
    setOwnerValidated(true); // nothing left to gate once idle
    update({ ...IDLE_STATE });
  }, [update]);

  // Privacy guard: drop any persisted job whose owner doesn't match the current
  // authenticated user. Fires on initial auth resolution (covering a rehydrated
  // job from sessionStorage) and on every login / logout / account switch, so a
  // completed result left in a shared tab can't be shown to a different user.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
      authResolvedRef.current = true;
      const uid = user?.uid || null;
      const current = jobRef.current;
      if (current.status === 'idle') return;

      if (current.ownerUid === AUTH_PENDING) {
        // Created before auth resolved in this session — it belongs to whoever the
        // session resolves to (this signed-in user, or null for a genuine guest).
        update({ ownerUid: uid });
      } else if (current.ownerUid !== uid) {
        // Owner doesn't match the current user: a concrete different uid (account
        // switch), a logout (uid null), or a real guest job (ownerUid null) now met
        // by a signed-in user. Drop it so one user can't see another's result.
        clearJob();
      }
      // Ownership of any rehydrated job has now been checked — safe to expose.
      setOwnerValidated(true);
    });
    return unsubscribe;
  }, [clearJob, update]);

  // Single app-wide poll loop. Runs whenever a job is pending, regardless of which
  // route is mounted, so completion is captured even while the user browses away.
  useEffect(() => {
    // Don't poll a rehydrated job until its owner is validated (avoids fetching a
    // possibly-other-user's result before the auth check runs).
    if (!ownerValidated || job.status !== 'pending' || !job.jobId) {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;

    const poll = async () => {
      const current = jobRef.current;
      if (cancelled || current.status !== 'pending' || !current.jobId) {
        return;
      }

      if (current.pollCount >= MAX_POLL_ATTEMPTS) {
        update({ status: 'failed', error: 'Request timed out. Please try again.' });
        return;
      }

      try {
        const auth = getAuth();
        const user = auth.currentUser;
        const token = user ? await user.getIdToken() : '';

        const res = await axios.get(`${BASE_URL}/job_status?job_id=${current.jobId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        // The job may have been cleared/changed while this request was in flight.
        if (cancelled || jobRef.current.jobId !== current.jobId) {
          return;
        }

        failuresRef.current = 0;
        const { status, result, data, error, progress } = res.data || {};

        if (status === 'completed') {
          const payload = result || data;
          if (payload && typeof payload === 'object') {
            update({ status: 'completed', result: payload, pollCount: current.pollCount + 1 });
          } else {
            // Completed but no usable result — fail instead of leaving the app stuck
            // in a "ready" state the UI can never consume (badge on, nothing to render).
            update({
              status: 'failed',
              error: 'Stack generation returned no result. Please try again.',
              pollCount: current.pollCount + 1,
            });
          }
        } else if (status === 'failed') {
          update({
            status: 'failed',
            error: error || 'Stack generation failed. Please try again.',
            pollCount: current.pollCount + 1,
          });
        } else if (status === 'not_found') {
          update({ status: 'expired', error: 'This stack generation expired. Please try again.' });
        } else {
          // status === 'pending' (or any benign in-progress state) — keep waiting.
          // Keep the last known progress when a poll happens to omit it.
          update({
            pollCount: current.pollCount + 1,
            ...(progress ? { progress } : {}),
          });
        }
      } catch (err) {
        if (cancelled || jobRef.current.jobId !== current.jobId) {
          return;
        }
        // The job row was evicted server-side (TTL / cache miss) — treat as expired,
        // not a hard error, so the user gets a clean "try again" rather than a scary failure.
        if (err?.response?.status === 404) {
          update({ status: 'expired', error: 'This stack generation expired. Please try again.' });
          return;
        }

        failuresRef.current += 1;
        if (failuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
          update({ status: 'failed', error: 'Unable to check stack status. Please try again.' });
        } else {
          // Transient blip — count the attempt against the ceiling but keep polling.
          update({ pollCount: jobRef.current.pollCount + 1 });
        }
      }
    };

    // Self-scheduling loop: the next poll is queued only AFTER the current request
    // settles, so a slow (>3s) job_status call can never overlap with the next tick
    // (which a fixed setInterval would allow — racing state and inflating pollCount).
    const runLoop = async () => {
      await poll();
      if (!cancelled && jobRef.current.status === 'pending' && jobRef.current.jobId) {
        pollTimerRef.current = setTimeout(runLoop, POLL_INTERVAL_MS);
      }
    };

    // Poll immediately so a returning user with an already-finished job sees the
    // result without waiting a full interval, then continue on a no-overlap cadence.
    runLoop();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [job.status, job.jobId, ownerValidated, update]);

  // While a rehydrated job is awaiting owner validation, present it to consumers as
  // idle so nothing (result, ready-dot, pending state) renders until it's cleared.
  const gated = !ownerValidated;

  const value = {
    // Live job state
    jobId: gated ? null : job.jobId,
    healthGoal: gated ? '' : job.healthGoal,
    // 'generate' | 'analyze' — see IDLE_STATE. A job rehydrated from an older
    // session has no `kind`; it can only have been a generation, so read it as
    // one rather than leaving it undefined for consumers to guess at.
    kind: gated ? 'generate' : (job.kind || 'generate'),
    // See IDLE_STATE. Consumers compare it with their own key; null matches
    // nothing a form would choose, so an unattributed job is nobody's.
    startedBy: gated ? null : (job.startedBy ?? null),
    status: gated ? 'idle' : job.status,
    result: gated ? null : job.result,
    pollCount: gated ? 0 : job.pollCount,
    error: gated ? null : job.error,
    progress: gated ? null : job.progress,
    // Derived convenience flags
    pendingJobId: !gated && job.status === 'pending' ? job.jobId : null,
    isPending: !gated && job.status === 'pending',
    isStarting: !gated && job.status === 'starting',
    // In-progress = kicking off OR actively polling. Drives the amber/pulsing dot
    // and the one-generation-at-a-time guard.
    isInProgress: !gated && (job.status === 'starting' || job.status === 'pending'),
    isReady: !gated && job.status === 'completed',
    // Actions
    startJob,
    beginGenerating,
    clearJob,
    // Back-compat aliases for existing callers
    setPendingJob: startJob,
    clearPendingJob: clearJob,
  };

  return (
    <StackGenerationContext.Provider value={value}>
      {children}
    </StackGenerationContext.Provider>
  );
};

export default StackGenerationContext;
